import {
    Button, Card, Form, Input, Modal, Select, Space, Table,
} from 'antd';
import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    ArrowRightOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined,
} from '@ant-design/icons';
import confirm from 'antd/lib/modal/confirm';
import { useForm } from 'antd/lib/form/Form';
import { IKnowledge } from './knowledge';

interface IAgent {
    id: string;
    name: string;
    created_at: number;
    ollama?: Object;
    info?: {
        base_model_id: string | null;
        meta?: {
            capabilities?: {
                web_search: boolean;
            };
            knowledge?: {
                id: string;
                name: string;
                type: string;
                description: string;
            }[];
            prompts?: string[];
        }
    }
}

const AiAgentsListPage = () => {
    const [form] = useForm();
    const [knowledge, setKnowledge] = useState<IKnowledge[]>([]);
    const [prompts, setPrompts] = useState<any[]>([]);
    const [list, setList] = useState<IAgent[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<string>();

    const onRowDeleteConfirmed = async (record: IAgent) => {
        setLoading(true);
        try {
            await axios({
                url: 'https://ai.retechcrm.ru/api/v1/models/model/delete',
                headers: {
                    Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                },
                method: 'POST',
                data: {
                    id: record.id,
                },
            });
        } finally {
            getList();
        }
    };

    const onDeleteClick = (record: IAgent) => {
        confirm({
            closable: true,
            title: 'Вы действительно хотите удалить выбранную строку?',
            onOk: () => onRowDeleteConfirmed(record),
            okType: 'danger',
            cancelText: 'Отмена',
            okText: 'Удалить',
        });
    };

    const actions = (record: IAgent) => (
        <Space direction="horizontal">
            <Button
                icon={<EditOutlined />}
                onClick={() => setSelected(record.id)}
                size="small"
            />
            <Button
                icon={<DeleteOutlined />}
                onClick={() => onDeleteClick(record)}
                size="small"
            />
            <Link to={`/ai-agents/${record.id}/chat`}>
                <Button type="primary" icon={<ArrowRightOutlined />} size="small" />
            </Link>
        </Space>
    );

    const closeModal = () => setSelected(undefined);

    const getKnowledge = async () => {
        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/v1/knowledge/',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
        });
        if (response.status === 200 && Array.isArray(response.data?.items)) {
            setKnowledge(response.data.items);
        }
    };

    const getPrompts = async () => {
        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/v1/prompts/',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
        });
        if (response.status === 200 && Array.isArray(response.data)) {
            setPrompts(response.data);
        }
    };

    const getList = async () => {
        setLoading(true);
        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/v1/models',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
        });
        if (response.status === 200 && response.data instanceof Object) {
            setList(response.data.data.filter((el: any) => !el.arena));
        }
        setLoading(false);
    };

    const extra = (
        <div className="card-extra-content">
            <div className="card-extra-layout-content justify-content-end">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setSelected('new')}
                />
            </div>
        </div>
    );

    useEffect(() => {
        getList();
        getKnowledge();
        getPrompts();
    }, []);

    useEffect(() => {
        if (typeof selected === 'string') {
            if (selected === 'new') {
                form.resetFields();
                form.setFieldsValue({
                    knowledge: [],
                    web_search: false,
                });
            } else {
                const row = list.find((el) => el.id === selected);
                form.setFieldsValue({
                    ...row,
                    base_model_id: row?.info?.base_model_id || undefined,
                    knowledge: row?.info?.meta?.knowledge?.map((el) => el.id) || [],
                    web_search: row?.info?.meta?.capabilities?.web_search || false,
                    prompts: row?.info?.meta?.prompts || [],
                });
            }
        }
    }, [selected]);

    const onSaveClick = async (values: any) => {
        const record = list.find((el) => el.id === selected);
        setSaving(true);
        const response = await axios({
            url: record instanceof Object ? `https://ai.retechcrm.ru/api/v1/models/model/update?id=${record.id}` : 'https://ai.retechcrm.ru/api/v1/models/create',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
            method: 'POST',
            data: {
                id: values.id,
                base_model_id: values.base_model_id,
                name: values.name,
                meta: {
                    profile_image_url: '/static/favicon.png',
                    description: null,
                    suggestion_prompts: null,
                    tags: [],
                    capabilities: {
                        file_context: true,
                        vision: true,
                        file_upload: true,
                        web_search: values.web_search,
                        image_generation: true,
                        code_interpreter: true,
                        terminal: true,
                        citations: true,
                        status_updates: true,
                        memory: true,
                        builtin_tools: true,
                    },
                    knowledge: values.knowledge.map((knowledgeId: string) => {
                        const item = knowledge.find((el) => el.id === knowledgeId);
                        return {
                            id: item?.id,
                            name: item?.name,
                            type: 'collection',
                            description: item?.description,
                        };
                    }),
                    prompts: values.prompts,
                },
                params: {
                    // temperature: 0.2,
                    // top_p: 0.9,
                    // top_k: 40,
                    // seed: 42,
                    // num_ctx: 8192,
                    // max_tokens: 2048,
                    // repeat_penalty: 1.1,
                    // stop: ['\nUser:', '<|end_of_text|>', '<|im_end|>'],

                    num_ctx: 16384,
                    num_predict: 2048,
                    num_gpu: 99,

                    system: values.prompts.map((promptId: string) => {
                        const item = prompts.find((el) => el.id === promptId);
                        return item?.content;
                    }).join('\n'),
                },
                // params: {
                //     system: values.knowledge.map((knowledgeId: string) => {
                //         const item = knowledge.find((el) => el.id === knowledgeId);
                //         return item?.description;
                //     }).join('\n'),
                // },
                access_grants: [],
            },
        });
        if (response.status === 200 && Array.isArray(response.data)) {
            setList(response.data);
        }
        getList();
        setSaving(false);
        closeModal();
    };

    return (
        <>
            <Scrollbars autoHide className="scrollable-content">
                <Space className="w-100" direction="vertical">
                    <Card className="page-object-horizontal-margin" title="Список ИИ-агентов" extra={extra}>
                        <Table
                            bordered
                            dataSource={list.filter((el) => typeof el.info?.base_model_id === 'string')}
                            columns={[{
                                dataIndex: 'id',
                                title: '#',
                                width: 240,
                            }, {
                                dataIndex: 'name',
                                title: 'Наименование',
                            }, {
                                align: 'center',
                                render: actions,
                                width: 60,
                            }]}
                            pagination={false}
                            loading={loading}
                        />
                    </Card>
                </Space>
            </Scrollbars>
            <Modal
                title={selected === 'new' ? 'Новый агент' : 'Редактирование агента'}
                visible={typeof selected === 'string'}
                onCancel={closeModal}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={onSaveClick}>
                    <Form.Item
                        label="ID модели"
                        name="id"
                        required
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Наименование"
                        name="name"
                        required
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Базовая модель"
                        name="base_model_id"
                        required
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={list.filter((el) => el.ollama instanceof Object).map((el) => ({ label: el.name, value: el.id }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Знания"
                        name="knowledge"
                    >
                        <Select
                            mode="multiple"
                            options={knowledge.map((el) => ({ label: el.name, value: el.id }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Промпты"
                        name="prompts"
                    >
                        <Select
                            mode="multiple"
                            options={prompts.map((el) => ({ label: el.name, value: el.id }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Веб-поиск"
                        name="web_search"
                    >
                        <Select options={[{ label: 'Да', value: true }, { label: 'Нет', value: false }]} />
                    </Form.Item>
                    <Button
                        htmlType="submit"
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                    >
                        Сохранить
                    </Button>
                </Form>
            </Modal>
        </>
    );
};

export default AiAgentsListPage;
